import { CellContext } from '@tanstack/react-table';
import { useLocale, useTranslate } from '@va/localization';
import { ParagraphWithTooltip, TypographyVariants } from '@va/ui/design-system';
import { TooltipWrapperProps } from '@va/ui/tooltips';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useMemo } from 'react';

type TextCellProps<T, P extends keyof TypographyVariants> = {
  className?: string;
  tooltipProps?: Partial<TooltipWrapperProps>;
  variant?: P;
  variantSize?: TypographyVariants[P];
  hideGroupedValueLabel?: boolean;
} & { getValue: () => string } & Omit<CellContext<T & { itemsInGroup?: string }, unknown>, 'getValue'>;

export function TextCellV8<T extends {}, P extends keyof TypographyVariants = 'interface'>({
  getValue,
  column,
  tooltipProps,
  row: {
    original: { itemsInGroup },
  },
  table: { getState },
  className,
  variant,
  variantSize,
  hideGroupedValueLabel = false,
}: TextCellProps<T, P>) {
  const state = getState();
  const isGrouped = column.getIsGrouped();
  const rawValue = getValue();
  const translate = useTranslate();
  const { locale } = useLocale();

  const typeofValue = useMemo(() => {
    const numberRegex = /^\d+$/;
    if (numberRegex.test(rawValue)) {
      return 'number';
    }
  }, [rawValue]);

  const valueAsNumber = useMemo(() => {
    return Number.parseInt(rawValue);
  }, [rawValue]);
  const isNumber = useMemo(() => typeofValue === 'number', [typeofValue]);

  const value = useMemo(() => {
    if (!isNumber) return rawValue;
    return addNumberSeparator(valueAsNumber, locale);
  }, [isNumber, locale, rawValue, valueAsNumber]);

  const othersGrouped = !isGrouped && state.grouping.length > 0;

  if (isGrouped && itemsInGroup) {
    if (isNil(itemsInGroup)) {
      return (
        <ParagraphWithTooltip {...tooltipProps} className={className} variant={variant} variantSize={variantSize}>
          {value}
        </ParagraphWithTooltip>
      );
    }

    return (
      <ParagraphWithTooltip
        {...tooltipProps}
        className={classNames(className, 'min-w-0 max-w-sm')}
        variant={variant}
        variantSize={variantSize}
      >
        {value} ({addNumberSeparator(Number.parseInt(itemsInGroup), locale)})
      </ParagraphWithTooltip>
    );
  }

  if (othersGrouped && !hideGroupedValueLabel) {
    return (
      <ParagraphWithTooltip {...tooltipProps} variant={variant} variantSize={variantSize}>
        {valueAsNumber === 1
          ? translate('eventTracking.table.value', {
              count: value,
            })
          : translate('eventTracking.table.values', {
              count: value,
            })}
      </ParagraphWithTooltip>
    );
  }

  return (
    <ParagraphWithTooltip
      {...tooltipProps}
      className={classNames(className, 'truncate')}
      variant={variant}
      variantSize={variantSize}
    >
      {value}
    </ParagraphWithTooltip>
  );
}
